import * as React from 'react'
import { Navigate, Outlet, useParams } from 'react-router-dom'
import { checkFeatures } from 'modules/core/hooks/useFeatures'
import { AvailableModules } from 'generated/featureDefinitions'
import {
  MyTrainingRequestsDocument,
  MyTrainingsCatalogDetailDocument,
  MyTrainingsDocument,
  MyTrainingsSurveysHeaderDocument,
  MyTrainingsTabsDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'
import {
  NavigationRoutesFn,
  buildModuleRoute,
  buildNavigation,
} from 'modules/core/lib/react-router'

import RequestHeader from 'modules/trainings/pages/MyTrainings/RequestHeader'
import { MyTrainingDetailHeader } from 'modules/trainings/pages/MyTrainings/Revamp/TrainingDetail/MyTrainingDetailHeader'
import { MyTrainingFormHeader } from 'modules/trainings/pages/MyTrainings/Surveys/MyTrainingFormHeader'

const MyTrainingsCertificatesPage = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Revamp/TrainingDetail/Certificates')
)

type Navigation = Parameters<NavigationRoutesFn>[0]
type Routes = ReturnType<NavigationRoutesFn>

const RootPage = React.lazy(() => import('modules/trainings/pages/MyTrainings/RootPage'))

export const myTrainingsRoutes = buildNavigation((navigation) => {
  return [
    buildModuleRoute({
      path: navigation.myTrainings.path,
      element: <RootPage />,
      handle: {
        module: 'my_trainings',
        moduleName: () => i18n.t('sidebar.my_trainings'),
        meta({ watchQuery, navigate, location }) {
          return watchQuery({
            query: MyTrainingsTabsDocument,
          }).map(({ data }) => {
            if (location.pathname === navigation.myTrainings.path) {
              navigate(navigation.myTrainings.revamp.overview.fullPath())
            }
            return {
              title: i18n.t('sidebar.my_trainings'),
              subNavigation: [
                {
                  title: i18n.t('trainings.my_trainings_revamp.overview.title'),
                  id: 'my_trainings',
                  href: navigation.myTrainings.revamp.overview.fullPath(),
                },
                {
                  title: i18n.t('trainings.my_training.catalog.title'),
                  id: 'catalog',
                  href: navigation.myTrainings.catalog.list.fullPath(),
                  isVisible: data?.trainings?._policies.sidebarSeeTrainingsCatalogSubLevelItem.can,
                },
                {
                  title: i18n.t('trainings.my_training.requests'),
                  id: 'requests',
                  href: navigation.myTrainings.requests.fullPath(),
                  isVisible: data?.trainings?._policies.sidebarSeeMyRequestsSubLevelItem.can,
                },
                {
                  title: i18n.t('trainings.my_training.surveys'),
                  id: 'surveys',
                  href: navigation.myTrainings.surveys.fullPath(),
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <Navigate to={navigation.myTrainings.catalog.list.fullPath()} />,
        },
        ...revamp(navigation),
        ...catalog(navigation),
        ...requests(navigation),
        ...surveys(navigation),
      ],
    }),
  ]
})

const MyTrainingIndexRevamp = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Revamp/Overview/MyTrainingIndexRevamp')
)
const MyTrainingsOverview = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Revamp/Overview')
)
const MyTrainingDetailIndexRevamp = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Revamp/TrainingDetail')
)
const MyTrainingDetailRevamp = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Revamp/TrainingDetail/Detail')
)
const MyTrainingMaterials = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Revamp/TrainingDetail/Materials')
)
const MyTrainingMaterialFileViewer = React.lazy(
  () =>
    import('modules/trainings/pages/MyTrainings/Revamp/TrainingDetail/Materials/TrainingMaterialFileViewer')
)
const Overview = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Revamp/TrainingDetail/Overview')
)
const SessionSidepanel = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Revamp/SessionSidepanel')
)
const MyTrainingUploadCertificate = React.lazy(
  () =>
    import('modules/trainings/pages/MyTrainings/Revamp/TrainingDetail/Certificates/MyTrainingUploadCertificateModal')
)

const Sessions = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Revamp/TrainingDetail/Sessions')
)
const MyTrainingsCertificateFileViewer = React.lazy(
  () =>
    import('modules/trainings/pages/MyTrainings/Revamp/TrainingDetail/Certificates/MyTrainingsCertificateFileViewer')
)
const ContentConsumerPage = React.lazy(() => import('modules/trainings/pages/content/Consumer'))
const ContentConsumerRoot = React.lazy(() => import('modules/trainings/pages/content/ConsumerRoot'))
const ContentOverviewPage = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Revamp/TrainingDetail/ContentOverview')
)

function revamp(navigation: Navigation): Routes {
  return [
    {
      path: navigation.myTrainings.revamp.path,
      handle: {
        meta: () => ({
          title: i18n.t('trainings.my_trainings_revamp.overview.title'),
          description: i18n.t('trainings.my_trainings_revamp.overview.description'),
        }),
      },
      children: [
        { index: true, element: <MyTrainingIndexRevamp /> },
        {
          path: navigation.myTrainings.revamp.overview.path,
          element: <MyTrainingsOverview />,
          children: [
            {
              path: navigation.myTrainings.revamp.overview.sidepanel.path,
              element: (
                <SessionSidepanel back={navigation.myTrainings.revamp.overview.fullPath()} />
              ),
            },
          ],
        },
        {
          path: navigation.myTrainings.revamp.detailsRevamp.path,
          handle: {
            resource: true,
            meta({ watchQuery, params, session }) {
              return watchQuery({
                query: MyTrainingsDocument,
                variables: {
                  trainingId: String(params.trainingId),
                  accessId: session.access.id,
                  employeeId: session.employee.id,
                  companyId: session.company.id,
                  companyIds: [session.company.id],
                },
              }).map(({ data }) => {
                const training = data?.trainings.trainingsConnection.nodes.find(
                  (node) => node.id === String(params.trainingId)
                )
                const membership = data?.trainings.trainingMembershipsConnection.nodes.find(
                  (node) => node.accessId === session.access.id
                )
                const membershipStatus = membership?.status
                const membershipId = membership?.id
                const isScormTraining = Boolean(training?.scormPackage)
                const { has_dev_trainings_scorm_cloud: hasScormFF } = checkFeatures(
                  ['dev_trainings_scorm_cloud'],
                  data?.features?.featuresConnection.nodes ?? []
                )

                const moduleAvailabilities =
                  data?.companies?.moduleAvailabilitiesConnection?.nodes || []
                const hasLMSModule = isModuleAvailabilityEnabled(
                  moduleAvailabilities,
                  AvailableModules.lms
                )

                const hasLMSEntitlement = hasLMSModule
                const contentEnabled =
                  hasLMSEntitlement &&
                  (data?.trainings?.contentModulesConnection?.totalCount ?? 0) >= 1

                if (!training) {
                  return { title: '' }
                }

                return {
                  headerComponent: (
                    <MyTrainingDetailHeader
                      contentEnabled={contentEnabled}
                      membershipStatus={membershipStatus ?? ''}
                      training={training}
                      isScormTraining={isScormTraining}
                      hasScormFF={hasScormFF}
                      membershipId={membershipId}
                    />
                  ),
                  title: training.name,
                  subNavigation: [
                    {
                      title: i18n.t('trainings.my_trainings_revamp.training_detail.tabs.overview'),
                      href: navigation.myTrainings.revamp.detailsRevamp.view.overview.fullPath({
                        trainingId: String(params.trainingId),
                      }),
                      index: true,
                    },
                    {
                      title: i18n.t('trainings.my_trainings_revamp.training_detail.tabs.content'),
                      href: navigation.myTrainings.revamp.detailsRevamp.view.contentOverview.fullPath(
                        {
                          trainingId: String(params.trainingId),
                        }
                      ),
                      isVisible: contentEnabled,
                    },
                    {
                      title: i18n.t('trainings.my_trainings_revamp.training_detail.tabs.materials'),
                      href: navigation.myTrainings.revamp.detailsRevamp.view.materials.fullPath({
                        trainingId: String(params.trainingId),
                      }),
                    },
                    {
                      title: i18n.t('trainings.my_trainings_revamp.training_detail.tabs.sessions'),
                      href: navigation.myTrainings.revamp.detailsRevamp.view.sessions.fullPath({
                        trainingId: String(params.trainingId),
                      }),
                    },
                    {
                      title: i18n.t(
                        'trainings.my_trainings_revamp.training_detail.tabs.certificates'
                      ),
                      href: navigation.myTrainings.revamp.detailsRevamp.view.certificates.fullPath({
                        trainingId: String(params.trainingId),
                      }),
                    },
                  ],
                }
              })
            },
          },
          children: [
            {
              index: true,
              element: <MyTrainingDetailIndexRevamp />,
            },
            {
              path: navigation.myTrainings.revamp.detailsRevamp.view.path,
              element: <MyTrainingDetailRevamp />,
              children: [
                {
                  path: navigation.myTrainings.revamp.detailsRevamp.view.overview.path,
                  element: <Overview />,
                },
                {
                  path: navigation.myTrainings.revamp.detailsRevamp.view.materials.path,
                  element: <MyTrainingMaterials />,
                  children: [
                    {
                      path: navigation.myTrainings.revamp.detailsRevamp.view.materials.file.path,
                      element: <MyTrainingMaterialFileViewer />,
                    },
                  ],
                },
                {
                  path: navigation.myTrainings.revamp.detailsRevamp.view.sessions.path,
                  element: <Sessions />,
                  children: [
                    {
                      path: navigation.myTrainings.revamp.detailsRevamp.view.sessions.sidepanel
                        .path,
                      element: <SessionSidepanel />,
                    },
                  ],
                },
                {
                  path: navigation.myTrainings.revamp.detailsRevamp.view.certificates.path,
                  element: <MyTrainingsCertificatesPage />,
                  children: [
                    {
                      path: navigation.myTrainings.revamp.detailsRevamp.view.certificates.view.path,
                      element: <MyTrainingsCertificateFileViewer />,
                    },
                    {
                      path: navigation.myTrainings.revamp.detailsRevamp.view.certificates.upload
                        .path,
                      element: <MyTrainingUploadCertificate />,
                    },
                  ],
                },
                {
                  path: navigation.myTrainings.revamp.detailsRevamp.view.contentOverview.path,
                  element: <ContentOverviewPage />,
                },
              ],
            },
            {
              path: navigation.myTrainings.revamp.detailsRevamp.content.path,
              element: <ContentConsumerRoot />,
              handle: {
                resource: true,
                meta: () => ({
                  title: i18n.t('trainings.my_trainings_revamp.training_detail.tabs.content'),
                  headerComponent: 'auto',
                  breadcrumb: i18n.t('trainings.my_trainings_revamp.training_detail.tabs.content'),
                }),
              },
              children: [
                {
                  index: true,
                  element: <ContentConsumerPage />,
                },
                {
                  path: navigation.myTrainings.revamp.detailsRevamp.content.block.path,
                  element: <ContentConsumerPage />,
                },
              ],
            },
          ],
        },
      ],
    },
  ]
}

const Catalog = React.lazy(() => import('modules/trainings/pages/MyTrainings/Catalog'))
const Wizard = React.lazy(() => import('modules/trainings/pages/MyTrainings/Requests/Wizard'))

const CatalogDetail = React.lazy(() => import('modules/trainings/pages/MyTrainings/Catalog/Detail'))
const CatalogDetailOverview = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Catalog/Detail/Overview')
)
const CatalogDetailSessions = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Catalog/Detail/Sessions')
)
const CatalogDetailSessionsSidePanel = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Catalog/Detail/Sessions/Sidepanel')
)
const CatalogDetailMaterials = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Catalog/Detail/Materials')
)
const CatalogFileViewer = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Catalog/Detail/Materials/FileViewer')
)
const TrainingCatalogHeader = React.lazy(
  () => import('modules/trainings/components/NewCatalogDataCollection/Header')
)
function catalog(navigation: Navigation): Routes {
  const WizardWrapper = () => {
    const { trainingId } = useParams()

    return (
      <FromCatalogWizard
        back={navigation.myTrainings.catalog.detail.overview.fullPath({
          trainingId: trainingId ?? '',
        })}
      />
    )
  }
  return [
    {
      path: navigation.myTrainings.catalog.path,
      element: <Outlet />,
      handle: {
        meta: () => ({
          title: i18n.t('trainings.my_training.catalog.title'),
        }),
      },
      children: [
        { index: true, element: <Navigate to={navigation.myTrainings.catalog.list.fullPath()} /> },
        {
          path: navigation.myTrainings.catalog.list.path,
          element: <Catalog />,
          children: [
            {
              path: navigation.myTrainings.catalog.list.fromCatalog.path,
              element: <FromCatalogWizard back={navigation.myTrainings.catalog.fullPath()} />,
            },
            {
              path: navigation.myTrainings.catalog.list.new.path,
              element: <Wizard back={navigation.myTrainings.catalog.fullPath()} />,
            },
          ],
        },
        {
          path: navigation.myTrainings.catalog.detail.path,
          element: <CatalogDetail />,
          handle: {
            resource: true,
            meta({ watchQuery, params, session }) {
              return watchQuery({
                query: MyTrainingsCatalogDetailDocument,
                fetchPolicy: 'cache-and-network',
                variables: {
                  trainingId: params.trainingId ?? '',
                  employeeId: session.employee.id,
                  companyId: session.company.id,
                },
              }).map(({ data }) => {
                const training = data.trainings.trainingsConnection.nodes.find(
                  (node) => String(node.id) === params.trainingId
                )

                if (!training) return { title: '' }
                const result = { trainingId: training.id, name: training.name }

                return {
                  headerComponent: <TrainingCatalogHeader training={result} />,
                  title: training.name,
                  subNavigation: [
                    {
                      title: i18n.t('trainings.my_trainings_revamp.training_detail.tabs.overview'),
                      index: true,
                      href: navigation.myTrainings.catalog.detail.overview.fullPath({
                        trainingId: training.id,
                      }),
                    },
                    {
                      title: i18n.t('trainings.my_trainings_revamp.training_detail.tabs.sessions'),
                      href: navigation.myTrainings.catalog.detail.sessions.fullPath({
                        trainingId: training.id,
                      }),
                    },
                    {
                      title: i18n.t('trainings.my_trainings_revamp.training_detail.tabs.materials'),
                      href: navigation.myTrainings.catalog.detail.materials.fullPath({
                        trainingId: training.id,
                      }),
                    },
                  ],
                }
              })
            },
          },
          children: [
            {
              path: navigation.myTrainings.catalog.detail.overview.path,

              element: <CatalogDetailOverview />,
            },
            {
              path: navigation.myTrainings.catalog.detail.fromCatalog.path,
              element: <WizardWrapper />,
            },
            {
              path: navigation.myTrainings.catalog.detail.sessions.path,
              element: <CatalogDetailSessions />,
              children: [
                {
                  path: navigation.myTrainings.catalog.detail.sessions.sidepanel.path,
                  element: <CatalogDetailSessionsSidePanel />,
                },
              ],
            },
            {
              path: navigation.myTrainings.catalog.detail.materials.path,
              element: <CatalogDetailMaterials />,
              children: [
                {
                  path: navigation.myTrainings.catalog.detail.materials.previewFile.path,
                  element: <CatalogFileViewer />,
                },
              ],
            },
          ],
        },
      ],
    },
  ]
}

const Requests = React.lazy(() => import('modules/trainings/pages/MyTrainings/Requests'))
const RequestsWizard = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Requests/Wizard')
)
const FromCatalogWizard = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Requests/FromCatalogWizard')
)
const RequestDetail = React.lazy(() => import('modules/trainings/pages/MyTrainings/RequestDetail'))
const RequestDetailIndexPage = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/RequestDetailIndexPage')
)
function requests(navigation: Navigation): Routes {
  return [
    {
      path: navigation.myTrainings.requests.path,
      handle: {
        meta: () => ({
          illustration: 'document-folder-board-edit',
          title: i18n.t('trainings.my_training.requests'),
          description: i18n.t('trainings.my_training.requests_description'),
        }),
      },
      children: [
        {
          element: <Navigate to={navigation.myTrainings.requests.list.fullPath()} />,
          index: true,
        },
        {
          path: navigation.myTrainings.requests.list.path,
          element: <Requests />,
          children: [
            {
              path: navigation.myTrainings.requests.list.new.path,
              element: <RequestsWizard back={navigation.myTrainings.requests.fullPath()} />,
            },
            {
              path: navigation.myTrainings.requests.list.fromCatalog.path,
              element: <FromCatalogWizard back={navigation.myTrainings.requests.fullPath()} />,
            },

          ],
        },
        {
          path: navigation.myTrainings.requests.detail.view.path,
          element: <RequestDetailIndexPage />,
          handle: {
            resource: true,
            meta({ watchQuery, params }) {
              return watchQuery({
                query: MyTrainingRequestsDocument,
                fetchPolicy: 'network-only',
                variables: {
                  ids: [String(params.id)],
                },
              }).map(({ data }) => {
                const request = data.trainings.trainingRequestsConnection.nodes.find(
                  (node) => String(node.id) === params.id
                )
                if (!request) {
                  return { title: '' }
                }
                return {
                  headerComponent: <RequestHeader myTrainingRequest={request} />,
                  title: request?.trainingName,
                }
              })
            },
          },
          children: [
            {
              path: navigation.myTrainings.requests.detail.view.path,
              element: <RequestDetail back={navigation.myTrainings.requests.fullPath()} />,
            },
          ],
        },
      ],
    },
    {
      // Redirect old link from notification email to requests list
      path: navigation.myTrainings.requestDetail.path,
      element: <Navigate to={navigation.myTrainings.requests.list.fullPath()} />,
    },
  ]
}

const MyTrainingSurveys = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Surveys/SurveysList')
)
const MyTrainingsSurveysIndex = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Surveys')
)
const MyTrainingSurveysScore = React.lazy(
  () => import('modules/trainings/pages/MyTrainings/Surveys/Score')
)
function surveys(navigation: Navigation): Routes {
  return [
    {
      path: navigation.myTrainings.surveys.path,
      element: <MyTrainingsSurveysIndex />,
      handle: {
        meta: () => ({
          title: 'My surveys',
        }),
      },
      children: [
        {
          index: true,
          element: <MyTrainingSurveys />,
        },
        {
          path: navigation.myTrainings.surveys.score.path,
          element: <MyTrainingSurveysScore />,
          handle: {
            resource: true,
            meta({ watchQuery, params }) {
              return watchQuery({
                query: MyTrainingsSurveysHeaderDocument,
                fetchPolicy: 'cache-and-network',
                variables: {
                  membershipId: String(params.membership_id),
                  materializedFormId: params.materialized_form_id ?? '',
                },
              }).map(({ data }) => {
                const target = data?.forms.membership
                const form = data?.forms.materializedForm
                const training = data?.trainings.trainingsConnection.nodes.find(
                  (node) => form?.form?.resourceId === String(node.id)
                )
                const score = data?.forms.answersConnection.nodes
                if (!score) {
                  return { title: '' }
                }
                if (!form) {
                  return { title: '' }
                }
                return {
                  headerComponent: (
                    <MyTrainingFormHeader
                      trainingName={training?.name ?? ''}
                      answers={false}
                      form={form}
                      target={target}
                    />
                  ),
                  title:
                    data?.forms.materializedForm?.form?.title ||
                    i18n.t('trainings.my_trainings_revamp.training_detail.title'),
                }
              })
            },
          },
        },
      ],
    },
  ]
}
